import { ReactElement, ReactNode, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import type { Mode } from 'react-hook-form';
import { AnyObjectSchema, InferType } from 'yup';

export type FormValues<Schema extends AnyObjectSchema> = InferType<Schema>;

interface FormProps<Schema extends AnyObjectSchema> {
  schema: Schema;
  id?: string;
  mode?: Mode;
  defaultValues: FormValues<Schema>;
  onSubmit: (data: InferType<Schema>) => Awaited<void>;
  children: ReactNode | ReactElement;
  resetOnDefaultChanges?: boolean;
  stopPropagation?: boolean;
  noValidate?: boolean;
  resolverOptions?: Parameters<typeof yupResolver>[1];
}

export const Form = <Schema extends AnyObjectSchema>({
  id,
  mode = 'all',
  schema,
  defaultValues,
  onSubmit,
  children,
  resetOnDefaultChanges,
  stopPropagation,
  noValidate,
  resolverOptions,
}: FormProps<Schema>) => {
  const methods = useForm<Schema>({
    defaultValues,
    resolver: yupResolver(schema),
    mode,
    context: resolverOptions?.context,
  });

  useEffect(() => {
    if (resetOnDefaultChanges) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods, resetOnDefaultChanges]);

  return (
    <FormProvider {...methods}>
      <form
        id={id}
        noValidate={noValidate}
        onSubmit={(event) => {
          methods.handleSubmit(onSubmit)(event);
          if (stopPropagation) {
            event.stopPropagation();
          }
        }}
      >
        {children}
      </form>
    </FormProvider>
  );
};
